import React, { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import mermaid from 'mermaid'
import { 
  FileText, 
  Search, 
  Book, 
  Code, 
  GitBranch, 
  Download,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  File,
  Folder,
  FolderOpen
} from 'lucide-react'

// Initialize Mermaid with dark theme configuration
mermaid.initialize({
  startOnLoad: true,
  theme: 'dark',
  themeVariables: {
    darkMode: true,
    primaryColor: '#3B82F6',
    primaryTextColor: '#FFFFFF',
    primaryBorderColor: '#1E40AF',
    lineColor: '#6B7280',
    secondaryColor: '#10B981',
    tertiaryColor: '#F59E0B',
    background: '#1F2937',
    mainBkg: '#374151',
    secondBkg: '#4B5563',
    tertiaryBkg: '#6B7280',
    nodeBorder: '#6B7280',
    clusterBkg: '#111827',
    clusterBorder: '#374151',
    defaultLinkColor: '#6B7280',
    titleColor: '#FFFFFF',
    edgeLabelBackground: '#1F2937',
    nodeTextColor: '#FFFFFF'
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  },
  sequence: {
    useMaxWidth: true,
    wrap: true
  },
  gantt: {
    useMaxWidth: true
  }
})

interface DocumentationFile {
  filename: string
  path: string
  title: string
  size: number
  modified: string
  has_mermaid: boolean
  code_blocks: number
  word_count: number
  preview?: string
}

interface DocumentationContent {
  filename: string
  content: string
  metadata: {
    title: string
    has_mermaid: boolean
    mermaid_diagrams?: Array<{
      index: number
      content: string
      type: string
      line_number: number
    }>
    code_blocks: number
    code_languages: string[]
  }
}

interface DocumentationViewerProps {
  isActive: boolean
}

const DocumentationViewer: React.FC<DocumentationViewerProps> = ({ isActive }) => {
  const [files, setFiles] = useState<DocumentationFile[]>([])
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [content, setContent] = useState<DocumentationContent | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statistics, setStatistics] = useState<any>(null)
  const mermaidRef = useRef<HTMLDivElement>(null)

  // Load documentation files on mount
  useEffect(() => {
    if (isActive) {
      loadDocumentationFiles()
      loadStatistics()
    }
  }, [isActive])

  // Render Mermaid diagrams when content changes
  useEffect(() => {
    if (content && content.metadata.has_mermaid) {
      renderMermaidDiagrams()
    }
  }, [content])

  const loadDocumentationFiles = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await fetch('/api/docs/list')
      if (!response.ok) throw new Error('Failed to load documentation files')
      
      const data = await response.json()
      setFiles(data.files)
      
      // Auto-select first file if none selected
      if (data.files.length > 0 && !selectedFile) {
        selectFile(data.files[0].filename)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files')
      console.error('Error loading documentation files:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/docs/stats')
      if (!response.ok) return
      
      const data = await response.json()
      setStatistics(data)
    } catch (err) {
      console.error('Error loading statistics:', err)
    }
  }

  const selectFile = async (filename: string) => {
    try {
      setSelectedFile(filename)
      setIsLoading(true)
      setError(null)
      
      const response = await fetch(`/api/docs/content/${encodeURIComponent(filename)}`)
      if (!response.ok) throw new Error('Failed to load file content')
      
      const data = await response.json()
      setContent(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content')
      console.error('Error loading file content:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    
    try {
      setIsSearching(true)
      const response = await fetch(`/api/docs/search?q=${encodeURIComponent(query)}`)
      if (!response.ok) return
      
      const data = await response.json()
      setSearchResults(data.results)
    } catch (err) {
      console.error('Search error:', err)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const renderMermaidDiagrams = async () => {
    if (!mermaidRef.current) return
    
    try {
      // Find all mermaid code blocks
      const mermaidElements = mermaidRef.current.querySelectorAll('.mermaid')
      console.log(`Found ${mermaidElements.length} Mermaid elements to render`)
      
      for (let i = 0; i < mermaidElements.length; i++) {
        const element = mermaidElements[i]
        const diagramContent = element.getAttribute('data-diagram') || element.textContent
        
        if (diagramContent) {
          try {
            console.log(`Rendering Mermaid diagram ${i}:`, diagramContent.substring(0, 100) + '...')
            const { svg } = await mermaid.render(`mermaid-${Date.now()}-${i}`, diagramContent)
            element.innerHTML = svg
            
            // Add some styling to make sure the SVG is visible
            const svgElement = element.querySelector('svg')
            if (svgElement) {
              svgElement.style.maxWidth = '100%'
              svgElement.style.height = 'auto'
            }
          } catch (err) {
            console.error('Mermaid rendering error:', err)
            element.innerHTML = `<div class="text-red-400 p-4 bg-red-900/20 rounded-lg border border-red-700">
              <strong>🚨 Mermaid Diagram Error:</strong><br/>
              ${err instanceof Error ? err.message : 'Unknown error'}<br/>
              <small class="text-red-300">Diagram content: ${diagramContent.substring(0, 200)}...</small>
            </div>`
          }
        } else {
          console.warn(`Mermaid element ${i} has no content`)
        }
      }
    } catch (err) {
      console.error('Error rendering Mermaid diagrams:', err)
    }
  }

  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath)
    } else {
      newExpanded.add(folderPath)
    }
    setExpandedFolders(newExpanded)
  }

  const organizeFilesByFolder = (files: DocumentationFile[]) => {
    const folders: { [key: string]: DocumentationFile[] } = {}
    
    files.forEach(file => {
      const pathParts = file.path.split('/')
      const folder = pathParts.slice(0, -1).join('/') || 'root'
      
      if (!folders[folder]) {
        folders[folder] = []
      }
      folders[folder].push(file)
    })
    
    return folders
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const customComponents = {
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '')
      const language = match ? match[1] : ''
      
      if (language === 'mermaid') {
        return (
          <div className="mermaid my-4" data-diagram={String(children).replace(/\n$/, '')}>
            {String(children).replace(/\n$/, '')}
          </div>
        )
      }
      
      return !inline ? (
        <div className="my-4">
          <div className="bg-secondary-700 px-4 py-2 text-sm text-secondary-300 rounded-t-lg flex justify-between items-center">
            <span>{language || 'text'}</span>
            <button
              onClick={() => navigator.clipboard.writeText(String(children))}
              className="text-xs hover:text-primary-400 transition-colors"
            >
              Copy
            </button>
          </div>
          <pre className="bg-secondary-800 p-4 rounded-b-lg overflow-x-auto">
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        </div>
      ) : (
        <code className="bg-secondary-700 px-1 py-0.5 rounded text-sm" {...props}>
          {children}
        </code>
      )
    },
    h1: ({ children }: any) => (
      <h1 className="text-3xl font-bold text-primary-400 mb-6 border-b border-secondary-700 pb-2">
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="text-2xl font-semibold text-primary-300 mb-4 mt-8">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-xl font-medium text-secondary-200 mb-3 mt-6">
        {children}
      </h3>
    ),
    p: ({ children }: any) => (
      <p className="text-secondary-300 mb-4 leading-relaxed">
        {children}
      </p>
    ),
    ul: ({ children }: any) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-secondary-300">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-secondary-300">
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="ml-4">
        {children}
      </li>
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-primary-500 pl-4 my-4 italic text-secondary-400 bg-secondary-800/30 p-4 rounded-r-lg">
        {children}
      </blockquote>
    ),
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full bg-secondary-800 rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    th: ({ children }: any) => (
      <th className="px-4 py-2 bg-secondary-700 text-left text-sm font-medium text-secondary-200">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="px-4 py-2 border-t border-secondary-700 text-secondary-300">
        {children}
      </td>
    ),
    a: ({ href, children }: any) => (
      <a 
        href={href} 
        className="text-primary-400 hover:text-primary-300 underline transition-colors"
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    )
  }

  const organizedFiles = organizeFilesByFolder(files)

  if (!isActive) return null

  return (
    <div className="h-full flex bg-gradient-to-br from-secondary-900 to-secondary-800">
      {/* Sidebar */}
      <div className="w-80 bg-secondary-800 border-r border-secondary-700 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-secondary-700">
          <div className="flex items-center space-x-2 mb-4">
            <Book className="w-6 h-6 text-primary-400" />
            <h2 className="text-xl font-bold text-white">Documentation</h2>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Search Results */}
        {searchQuery && (
          <div className="p-4 border-b border-secondary-700 max-h-48 overflow-y-auto">
            {isSearching ? (
              <div className="text-secondary-400 text-sm">Searching...</div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => selectFile(result.filename)}
                    className="p-2 bg-secondary-700/50 rounded cursor-pointer hover:bg-secondary-700 transition-colors"
                  >
                    <div className="font-medium text-white text-sm">{result.title}</div>
                    <div className="text-secondary-400 text-xs mt-1">{result.preview}</div>
                    <div className="text-primary-400 text-xs mt-1">Score: {result.score}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-secondary-400 text-sm">No results found</div>
            )}
          </div>
        )}

        {/* File Tree */}
        <div className="flex-1 overflow-y-auto p-4">
          {Object.entries(organizedFiles).map(([folder, folderFiles]) => {
            const isExpanded = expandedFolders.has(folder)
            const hasMermaid = folderFiles.some(f => f.has_mermaid)
            
            return (
              <div key={folder} className="mb-2">
                <div
                  onClick={() => toggleFolder(folder)}
                  className="flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-secondary-700 transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-secondary-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-secondary-400" />
                  )}
                  {isExpanded ? (
                    <FolderOpen className="w-4 h-4 text-secondary-400" />
                  ) : (
                    <Folder className="w-4 h-4 text-secondary-400" />
                  )}
                  <span className="text-sm font-medium text-secondary-200">
                    {folder === 'root' ? 'Root' : folder.split('/').pop()}
                  </span>
                  {hasMermaid && <GitBranch className="w-3 h-3 text-primary-400" />}
                </div>
                
                {isExpanded && (
                  <div className="ml-6 space-y-1">
                    {folderFiles.map((file) => (
                      <div
                        key={file.filename}
                        onClick={() => selectFile(file.filename)}
                        className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedFile === file.filename
                            ? 'bg-primary-600/20 border border-primary-500/30'
                            : 'hover:bg-secondary-700/50'
                        }`}
                      >
                        <File className="w-4 h-4 text-secondary-400" />
                        <span className="text-sm text-secondary-300 flex-1">
                          {file.title}
                        </span>
                        {file.has_mermaid && (
                          <GitBranch className="w-3 h-3 text-primary-400" />
                        )}
                        {file.code_blocks > 0 && (
                          <Code className="w-3 h-3 text-secondary-500" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="p-4 border-t border-secondary-700 bg-secondary-800/50">
            <div className="text-xs text-secondary-400 space-y-1">
              <div>Files: {statistics.total_files}</div>
              <div>Mermaid: {statistics.files_with_mermaid}</div>
              <div>Code blocks: {statistics.total_code_blocks}</div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Content Header */}
        <div className="bg-secondary-800 border-b border-secondary-700 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-primary-400" />
            <div>
              <h1 className="text-lg font-semibold text-white">
                {content?.metadata.title || 'Select a document'}
              </h1>
              {content && (
                <p className="text-sm text-secondary-400">
                  {content.filename}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={loadDocumentationFiles}
              className="p-2 bg-secondary-700 hover:bg-secondary-600 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-secondary-300" />
            </button>
            <button
              className="p-2 bg-secondary-700 hover:bg-secondary-600 rounded-lg transition-colors"
              title="Export"
            >
              <Download className="w-4 h-4 text-secondary-300" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
                <p className="text-secondary-400">Loading documentation...</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-red-400">
                <p className="mb-2">❌ Error loading documentation</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : content ? (
            <div className="h-full overflow-y-auto">
              <div 
                ref={mermaidRef}
                className="p-8 prose prose-invert prose-lg max-w-none"
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight, rehypeRaw]}
                  components={customComponents}
                >
                  {content.content}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-secondary-400">
                <Book className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a documentation file to view its content</p>
                <p className="text-sm mt-2">Your Mermaid diagrams will be beautifully rendered here!</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DocumentationViewer